/**
 * The Waben-Bingo board as a honeycomb (amendment 20): rows of hexagons, wide and narrow in
 * turn so that they interlock, with the beer in the middle and the players' fields around it.
 *
 * Pure geometry in pixels of the board — where each field sits, where the beer sits, how big
 * the board is. The fields keep their order, row by row; only the middle is taken out for the
 * beer, so a field nobody touches stays where it was when the next one is written. Only the
 * front end draws it; it lives here so that it can be tested.
 */

export type Slot = { x: number; y: number };

export type Honeycomb = {
  centre: Slot;
  fields: Slot[];
  width: number;
  height: number;
  /** One hexagon, pointy side up. */
  hex: { width: number; height: number };
};

/** Hexagons in a wide row; a narrow one holds one fewer. A phone has room for four. */
export const COLUMNS = 4;

export function honeycomb(count: number, size = 72, gap = 4, columns = COLUMNS): Honeycomb {
  const hex = { width: size, height: (size * 2) / Math.sqrt(3) };
  const step = { x: size + gap, y: hex.height * 0.75 + gap };
  const total = count + 1;

  // How many go in each row: wide and narrow in turn, until there is room for all.
  const rows: number[] = [];
  for (let placed = 0; placed < total; ) {
    const room = rows.length % 2 === 0 ? columns : columns - 1;
    const here = Math.min(room, total - placed);
    rows.push(here);
    placed += here;
  }

  const slots: Slot[] = [];
  rows.forEach((here, row) => {
    const wide = row % 2 === 0;
    const room = wide ? columns : columns - 1;
    // A short last row moves in by whole steps only: half a step would put it out of the
    // honeycomb, straight above the row before.
    const inset = Math.floor((room - here) / 2) * step.x + (wide ? 0 : step.x / 2);
    for (let column = 0; column < here; column += 1) {
      slots.push({ x: inset + column * step.x, y: row * step.y });
    }
  });

  // The board is as wide as its hexagons and no wider. Three in a row kept the room of a
  // fourth, and the card that centres the board showed them off to one side.
  const left = Math.min(...slots.map((slot) => slot.x));
  const right = Math.max(...slots.map((slot) => slot.x));
  for (const slot of slots) slot.x -= left;
  const width = right - left + hex.width;
  const height = (rows.length - 1) * step.y + hex.height;

  const middle = { x: (width - hex.width) / 2, y: (height - hex.height) / 2 };
  let centre = 0;
  slots.forEach((slot, index) => {
    const best = slots[centre] ?? slot;
    const distance = Math.hypot(slot.x - middle.x, slot.y - middle.y);
    if (distance < Math.hypot(best.x - middle.x, best.y - middle.y)) centre = index;
  });

  return {
    centre: slots[centre] ?? { x: 0, y: 0 },
    fields: slots.filter((_, index) => index !== centre),
    width,
    height,
    hex,
  };
}
